import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Carousel from '../Carousel';
import { CheckIcon } from '../icons';
import DiscountPrice from './DiscountPrice';
import { COMPARE_DATA, CARD_DISPLAY, AWNING_POPULAR_SLUG } from '../../data/homepageData';
import './ProductCard.css';
import { cardImages } from '../../utils/productImages';

// Number of bullets shown on the card. The full list lives on the product
// page - the card is a summary, and more than two makes the row uneven.
const MAX_BULLETS = 2;
// The thumbnail strip holds four. A product with a long gallery would
// otherwise shrink its thumbnails to slivers; the full gallery is on the
// product page.
const MAX_CARD_IMAGES = 4;

// Small line icons for the spec tiles. Kept here rather than in the shared
// icons module because they only exist to label these tiles.
const TILE_ICONS = {
  colours: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <circle cx="9" cy="12" r="6" /><circle cx="15" cy="12" r="6" />
    </svg>
  ),
  led: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M9 18h6M10 21h4M12 3a6 6 0 0 1 4 10.5V16H8v-2.5A6 6 0 0 1 12 3Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  motor: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <rect x="3" y="8" width="18" height="9" rx="2" /><path d="M7 8V6h10v2M12 12v2" strokeLinecap="round" />
    </svg>
  ),
  projection: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M4 5v14M4 8h13l3 4-3 4H4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  arms: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M3 6h18M6 6l5 6-3 6M18 6l-5 6 3 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  crank: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M6 4v10a4 4 0 0 0 8 0V9" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="6" cy="18" r="2" /><circle cx="14" cy="7" r="1.6" />
    </svg>
  ),
  valance: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M3 7h18v6a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7Z" /><path d="M7 16v2M12 16v2M17 16v2" strokeLinecap="round" />
    </svg>
  ),
};

/**
 * Homepage product card: a large lead image with a thumbnail strip, then the
 * product's copy, spec tiles, price and calls to action.
 *
 * The card owns its own gallery index and passes it down to the shared
 * Carousel in controlled mode, so a thumbnail click and a swipe both move the
 * same single piece of state. That keeps one carousel implementation for the
 * whole site rather than a second swipe engine living in here.
 */
export default function ProductCard({ product }) {
  const [imageIndex, setImageIndex] = useState(0);

  const slug = product.slug || product._id || '';
  const meta = COMPARE_DATA[slug] || {};
  const display = CARD_DISPLAY[slug] || {};
  // The card's strip comes from the shared resolver, so this card and every
  // other card on the site lead with the same photo.
  const images = cardImages(product, MAX_CARD_IMAGES);
  const isPopular = slug === AWNING_POPULAR_SLUG;
  const bullets = (meta.bullets || []).slice(0, MAX_BULLETS);

  // Operation is derived from COMPARE_DATA rather than restated in
  // CARD_DISPLAY, so the overlay label, the spec tile and the bullet list all
  // move together. Three of the five awnings are hand-operated.
  const operation = meta.motorised ? 'Motorised' : 'Manual';
  const operationTile = meta.motorised
    ? { top: 'Motorised', bottom: 'Remote + Wind Sensor', icon: 'motor' }
    : { top: 'Manual', bottom: 'Hand Crank', icon: 'crank' };

  // The B series links its arms to one another; the A and K series run
  // independent arms. It is the main mechanical difference across the range, so
  // it earns a tile of its own rather than sitting buried in the spec table.
  const armsTile = meta.linkedArms
    ? { top: 'Linked', bottom: 'Chain Link Arms', icon: 'arms' }
    : { top: 'Independent', bottom: 'Unlinked Arms', icon: 'arms' };

  const overlay = [operation, display.feature].filter(Boolean);

  // The B series is the new generation; A09 and K3 are the previous one.
  // AW01 is deliberately unlabelled - the cassette is its own line rather than
  // a point on that timeline.
  const generation = meta.generation === 'new' ? 'New Generation'
    : meta.generation === 'previous' ? 'Previous Generation'
    : null;
  const tiles = display.tiles ? [...display.tiles, operationTile, armsTile] : [];

  const handleThumb = useCallback((i) => setImageIndex(i), []);

  const renderSlide = (src, i) => (
    <div className="pc-slide">
      <img
        src={src}
        alt={`${product.name} - view ${i + 1}`}
        width={600}
        height={400}
        loading={i === 0 ? 'eager' : 'lazy'}
        draggable={false}
      />
      {i === 0 && overlay.length > 0 && (
        <div className="pc-overlay">
          {overlay.map((label, k) => (
            <span key={k} className="pc-overlay-label">{label}</span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <article className="pc-card">
      {isPopular && <span className="pc-badge">Most Popular</span>}

      <div className="pc-gallery">
        {images.length > 0 ? (
          <Carousel
            ariaLabel={`${product.name} images`}
            items={images}
            renderItem={renderSlide}
            visibleCount={1}
            gap={0}
            index={imageIndex}
            onIndexChange={setImageIndex}
            className="pc-carousel"
          />
        ) : (
          <div className="pc-img-placeholder" />
        )}
      </div>

      {/* Thumbnail strip. Hidden when there is only one image, so a product
          without a full set does not show a lone stranded thumbnail. */}
      {images.length > 1 && (
        <div className="pc-thumbs" role="tablist" aria-label={`${product.name} image thumbnails`}>
          {images.map((src, i) => (
            <button
              key={src}
              type="button"
              role="tab"
              aria-selected={i === imageIndex}
              aria-label={`Show image ${i + 1} of ${images.length}`}
              className={`pc-thumb${i === imageIndex ? ' pc-thumb--active' : ''}`}
              onClick={() => handleThumb(i)}
            >
              <img src={src} alt="" width={120} height={80} loading="lazy" draggable={false} />
            </button>
          ))}
        </div>
      )}

      <div className="pc-body">
        <h3 className="pc-name">{product.name}</h3>
        <p className="pc-meta">
          {meta.tagline && <span className="pc-tagline">{meta.tagline}</span>}
          {generation && (
            <span className={`pc-gen pc-gen--${meta.generation}`}>{generation}</span>
          )}
        </p>
        {/* One line only. The full description stays on the product page -
            a card is a summary, and five paragraphs side by side is a wall. */}
        {display.blurb && <p className="pc-desc">{display.blurb}</p>}

        {bullets.length > 0 && (
          <ul className="pc-bullets">
            {bullets.map((b, i) => (
              <li key={i}><CheckIcon />{b}</li>
            ))}
          </ul>
        )}

        {tiles.length > 0 && (
          <div className="pc-tiles">
            {tiles.map((t, i) => (
              <div className="pc-tile" key={i}>
                <span className="pc-tile-icon" aria-hidden="true">{TILE_ICONS[t.icon]}</span>
                <span className="pc-tile-text">
                  <span className="pc-tile-top">{t.top}</span>
                  <span className="pc-tile-bottom">{t.bottom}</span>
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="pc-foot">
          <DiscountPrice product={product} prefix="from" />
          <div className="pc-actions">
            <Link to={`/products/${slug}`} className="pc-btn">View Details</Link>
            <Link to="/products/compare" className="pc-compare">
              Compare
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
